import React, { useEffect, useState, useCallback } from 'react';
import { sarAPI, payrollAPI } from '../../lib/api';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../lib/errors';
import { formatCents } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { StatCard } from '../../components/import/ImportPrimitives';
import { ImportPicker } from './ImportPicker';
import { DollarSign, Loader2, ShieldCheck, CheckCircle2, AlertTriangle, Lock, Download } from 'lucide-react';

const cents = (c) => formatCents(c);
// A rate string ("18.5") rendered as money with a thousands separator.
const money = (v) => {
  if (v === null || v === undefined || v === '' || v === 'None') return '—';
  const num = Number(v);
  return Number.isNaN(num) ? String(v)
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

export default function GrossPayParity() {
  const [imports, setImports] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [method, setMethod] = useState('actual');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    sarAPI.listImports(true)
      .then(({ data }) => {
        const imps = data.imports || [];
        setImports(imps);
        const preferred = imps.find((i) => i.is_final) || imps[0];
        if (preferred) setSelectedId(preferred.id);
      })
      .catch((e) => toast.error(extractErrorMessage(e, 'Could not load imports')));
  }, []);

  const run = useCallback(async (id, m) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await payrollAPI.parity(id, m);
      setData(data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not compute gross pay parity'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedId) run(selectedId, method); }, [selectedId, method, run]);

  const doExport = useCallback(async (fmt) => {
    if (!selectedId) return;
    try {
      const { data } = await payrollAPI.rateParityExport(selectedId, fmt, method);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rate_parity_${selectedId}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported rate parity (${fmt.toUpperCase()})`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not export rate parity'));
    }
  }, [selectedId, method]);

  const t = data?.totals || {};
  const restricted = data?.restricted_note;

  return (
    <div className="space-y-6" data-testid="sar-grosspay-tab">
      <Alert className="border-sky-200 bg-sky-50" data-testid="sar-grosspay-readonly">
        <ShieldCheck className="h-4 w-4 text-sky-600" />
        <AlertDescription className="text-sky-800 text-sm">
          <strong>Read-only parallel test.</strong> TriCorps calculates gross pay from the SAR and
          compares its regular and overtime dollars with WinTeam's, per employee and pay period.
          Nothing here writes to pay runs, the ledger, invoices, schedules or timesheets.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" /> Gross pay parity</CardTitle>
          <CardDescription>Choose a final import, then the overtime method to compare under.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ImportPicker imports={imports} value={selectedId} onChange={setSelectedId} testId="sar-grosspay-import-picker" />
          <div className="flex items-center gap-2" data-testid="sar-grosspay-method">
            <span className="text-sm text-slate-600">Overtime method:</span>
            <Button size="sm" variant={method === 'actual' ? 'default' : 'outline'}
              onClick={() => setMethod('actual')} data-testid="sar-grosspay-method-actual">
              1.5× actual rate
            </Button>
            <Button size="sm" variant={method === 'weighted_average' ? 'default' : 'outline'}
              onClick={() => setMethod('weighted_average')} data-testid="sar-grosspay-method-weighted">
              FLSA weighted average
            </Button>
          </div>
        </CardContent>
      </Card>

      {restricted && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="sar-grosspay-restricted">
          <Lock className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 text-sm">{restricted}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="sar-grosspay-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Computing gross pay…
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="TriCorps regular $" value={cents(t.tri_regular_cents)} tone="text-sky-700" testId="sar-gp-stat-tri-reg" />
            <StatCard label="TriCorps OT $" value={cents(t.tri_ot_cents)} tone="text-amber-700" testId="sar-gp-stat-tri-ot" />
            <StatCard label="WinTeam regular $" value={cents(t.sar_dollars_cents)} testId="sar-gp-stat-sar-reg" />
            <StatCard label="WinTeam OT $" value={cents(t.sar_ot_dollars_cents)} testId="sar-gp-stat-sar-ot" />
            <StatCard label="Employee-periods" value={String(t.employee_period_count ?? '—')} testId="sar-gp-stat-ep" />
            <StatCard label="Matches" value={String(t.employee_period_match_count ?? '—')} tone="text-emerald-700" testId="sar-gp-stat-match" />
            <StatCard label="Rows" value={String(t.row_count ?? '—')} testId="sar-gp-stat-rows" />
            <StatCard label="Rate-parity rows" value={String(t.rate_parity_count ?? '—')} tone="text-rose-700" testId="sar-gp-stat-rate" />
            <StatCard label="Premium-rule rows" value={String(t.premium_rule_count ?? '—')} tone="text-violet-700" testId="sar-gp-stat-premium" />
          </div>

          <Card data-testid="sar-grosspay-employees">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Gross pay by employee &amp; pay period</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(data.employees || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No employees to compare.</p>
              ) : (
                <div className="overflow-auto max-h-[480px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Pay period</TableHead>
                        <TableHead className="text-right">TriCorps reg $</TableHead>
                        <TableHead className="text-right">TriCorps OT $</TableHead>
                        <TableHead className="text-right">WinTeam reg $</TableHead>
                        <TableHead className="text-right">WinTeam OT $</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.employees.map((e) => (
                        <TableRow key={`${e.employee_number}-${e.pay_period_id}`} data-testid={`sar-gp-row-${e.employee_number}`} className={e.matched === false ? 'bg-rose-50' : (e.matches ? '' : 'bg-amber-50')}>
                          <TableCell className="font-mono text-xs">{e.employee_number}{e.employee_name ? ` · ${e.employee_name}` : ''}</TableCell>
                          <TableCell className="text-xs" data-testid={`sar-gp-period-${e.employee_number}`}>{e.pay_period_label || e.pay_period_id || '—'}</TableCell>
                          <TableCell className="text-right font-medium">{cents(e.tri_regular_cents)}</TableCell>
                          <TableCell className="text-right font-medium">{cents(e.tri_ot_cents)}</TableCell>
                          <TableCell className="text-right">{cents(e.sar_dollars_cents)}</TableCell>
                          <TableCell className="text-right">{cents(e.sar_ot_dollars_cents)}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1" data-testid={`sar-gp-status-${e.employee_number}`}>
                              {e.matched === false ? (
                                <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Unmatched employee</Badge>
                              ) : e.not_compared ? (
                                <Badge variant="secondary" className="text-sky-700 bg-sky-50" data-testid={`sar-gp-salaried-${e.employee_number}`}><CheckCircle2 className="w-3 h-3 mr-1" />Salaried (not compared)</Badge>
                              ) : e.matches ? (
                                <Badge variant="secondary" className="text-emerald-700 bg-emerald-50"><CheckCircle2 className="w-3 h-3 mr-1" />Match</Badge>
                              ) : (
                                <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />{e.status || (e.reasons || []).join(', ') || 'Differs'}</Badge>
                              )}
                              {(e.warnings || []).map((w, i) => (
                                <span key={i} className="text-[11px] text-amber-700 inline-flex items-center"><AlertTriangle className="w-3 h-3 mr-1" />{w}</span>
                              ))}
                              {e.premium_rule_applied && (
                                <Badge variant="outline" className="text-violet-700 border-violet-300 bg-violet-50 w-fit" data-testid={`sar-gp-premium-${e.employee_number}`}>
                                  Premium rule (no added OT)
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {(data.rate_parity || []).length > 0 && (
            <Card data-testid="sar-grosspay-rate-parity">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-500" /> Rate parity — fix before November 1
                    </CardTitle>
                    <CardDescription>Rows where the rate TriCorps resolves differs from the SAR PayRate or SpecialPayRate — one row per employee, job, service and rate pair, with the number of SAR rows and total hours behind it.</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => doExport('csv')} data-testid="sar-gp-rate-export-csv">
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => doExport('xlsx')} data-testid="sar-gp-rate-export-xlsx">
                      <Download className="w-4 h-4 mr-1" /> Excel
                    </Button>
                  </div>
                </div>
                {restricted && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="sar-gp-rate-redaction-note">
                    Some employees are hidden or their rates redacted; the export honours the same restriction.
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="overflow-auto max-h-[360px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Job</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead className="text-right">TriCorps rate</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">SAR PayRate</TableHead>
                        <TableHead className="text-right">SAR Special</TableHead>
                        <TableHead className="text-right">SAR rows</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const groups = [];
                        const index = {};
                        data.rate_parity.forEach((r) => {
                          const key = r.employee_number || '—';
                          if (index[key] === undefined) { index[key] = groups.length; groups.push({ key, name: r.employee_name, rows: [] }); }
                          groups[index[key]].rows.push(r);
                        });
                        return groups.map((g) => (
                          <React.Fragment key={g.key}>
                            <TableRow className="bg-slate-50" data-testid={`sar-gp-rate-group-${g.key}`}>
                              <TableCell colSpan={9} className="font-mono text-xs font-semibold text-slate-700">
                                {g.key}{g.name ? ` · ${g.name}` : ''} <span className="text-slate-400 font-normal">({g.rows.length} row{g.rows.length === 1 ? '' : 's'})</span>
                              </TableCell>
                            </TableRow>
                            {g.rows.map((r, i) => (
                              <TableRow key={`${g.key}-${r.job_number}-${i}`} data-testid={`sar-gp-rate-row-${r.employee_number}-${i}`}>
                                <TableCell></TableCell>
                                <TableCell className="text-xs">{r.job_number}</TableCell>
                                <TableCell className="text-xs">{r.billing_item}</TableCell>
                                <TableCell className="text-right">{money(r.tri_rate)}</TableCell>
                                <TableCell className="text-xs">{r.rate_source}</TableCell>
                                <TableCell className="text-right">{money(r.sar_pay_rate)}</TableCell>
                                <TableCell className="text-right">{money(r.sar_special_rate)}</TableCell>
                                <TableCell className="text-right text-xs">{r.sar_row_count ?? '—'}</TableCell>
                                <TableCell className="text-right text-xs">{r.hours ?? '—'}</TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
